import React from "react";
import { Link } from "react-router-dom";

const IMG_TALLOW = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/91f4csle_Pure%20Tallow%20soap.jpg";
const IMG_SALT = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/u7skbuj9_Alati%20signature%20salt%20bar%20tallow%20soap%20infused%20with%20Indigo%20powder.jpg";
const IMG_CACAO = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/2mrz3li6_Infused%20with%20Cacao%20Powder%20organic%20Tallow%20soap.jpg";

const Block = ({ children }) => (
    <section className="px-6 lg:px-10 py-12">
        <div className="max-w-2xl mx-auto">{children}</div>
    </section>
);

const WhyTraditionalSoap = () => {
    return (
        <div data-testid="tradition-page" className="bg-[#FAF9F6]">
            <section className="py-20 lg:py-28 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto">
                    <p className="label">Why Traditional Soap?</p>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-5 leading-[1.04]">
                        Made the old way. Trusted today.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-[#4A4A46] leading-relaxed">
                        ALATI specialises in handcrafted tallow soap made using traditional
                        soap-making methods. Alongside our tallow range we also produce small
                        seasonal batches of traditional Castile soap &mdash; which requires an
                        extended curing period &mdash; and our signature Sea Mineral Salt Bar,
                        inspired by our family&rsquo;s Greek heritage.
                    </p>
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img src={IMG_TALLOW} alt="Pure Tallow Soap" className="w-full h-full object-cover" />
                </div>
            </section>

            <Block>
                <p className="label">Why Tallow?</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    A traditional ingredient, used thoughtfully.
                </h2>
                <div className="space-y-5 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        Tallow has long been valued in traditional soap making because it
                        helps create a firm, creamy, long-lasting bar.
                    </p>
                    <p>
                        For most of human history, soap was made with what was available.
                        Olive oil in olive-growing regions, animal fats in farming communities.
                        Tallow soap is one of the oldest, most honest soap traditions there
                        is.
                    </p>
                    <p>
                        At ALATI, we use grass-fed tallow paired with olive, coconut and
                        castor oils. Nothing more complicated than that.
                    </p>
                    <p>
                        Tallow naturally contains fatty acids commonly found in animal fats,
                        and before saponification contains small amounts of fat-soluble
                        vitamins A, D, E and K. The finished soap is, simply, soap &mdash;
                        thoughtfully made.
                    </p>
                    <p className="italic text-[#1C1C1A]">
                        For me personally, returning to simple, properly made soap changed
                        the way my skin felt in everyday life. That is my experience, not a
                        promise. &mdash; Chrissa
                    </p>
                </div>
            </Block>

            <Block>
                <p className="label">Naturally Retained Glycerin</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    What handmade soap keeps that mass-produced bars don&rsquo;t.
                </h2>
                <div className="space-y-5 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        When oils and fats saponify into soap, glycerin is produced as a
                        natural by-product. In traditionally made handmade soap, that
                        glycerin stays in the bar.
                    </p>
                    <p>
                        Many mass-produced cleansing bars on supermarket shelves are not
                        traditional soap at all &mdash; they are detergent bars where the
                        glycerin has been removed and sold separately. We don&rsquo;t do that.
                        The glycerin stays where it belongs.
                    </p>
                </div>
            </Block>

            <section className="px-6 lg:px-10 py-12">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img src={IMG_CACAO} alt="Organic Cacao Tallow Soap" className="w-full h-full object-cover" />
                </div>
            </section>

            <Block>
                <p className="label">Castile Soap</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    Some things should never be rushed.
                </h2>
                <div className="space-y-5 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        Castile is traditional olive oil soap &mdash; the oldest form of soap
                        in our family&rsquo;s story. True Castile uses olive oil as its single
                        oil and cures slowly. Sometimes for many months. Sometimes close to a
                        year.
                    </p>
                    <p>
                        The bar that comes out the other side is mild, simple, and quietly
                        beautiful. It is the soap our grandmothers made.
                    </p>
                </div>
            </Block>

            <Block>
                <p className="label">Hot Process &amp; Cold Process</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    Two old methods. Both are ours.
                </h2>
                <div className="space-y-5 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        ALATI uses both hot process and cold process methods. Each has its
                        place.
                    </p>
                    <p>
                        <span className="text-[#1C1C1A] font-medium">Cold process</span>
                        &nbsp; allows the soap to saponify slowly at room temperature, then
                        cure on open racks for weeks. The finish is smooth and quietly
                        traditional.
                    </p>
                    <p>
                        <span className="text-[#1C1C1A] font-medium">Hot process</span>
                        &nbsp; cooks the soap through during making, producing a bar with a
                        different rustic character and a shorter cure time.
                    </p>
                    <p>
                        Both are old. Both are honest. Neither is rushed.
                    </p>
                </div>
            </Block>

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">Beautiful bars. Honest ingredients.</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                        Nothing unnecessary.
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <Link to="/shop" className="btn-primary" data-testid="tradition-shop-cta">
                            Shop Soap
                        </Link>
                        <Link to="/process#ingredients" className="btn-secondary" data-testid="tradition-ingredients-cta">
                            Inside Every Bar
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WhyTraditionalSoap;
